import React, { useId } from 'react';
import { MdExpandMore } from 'react-icons/md';
import clsx from 'clsx';
import {
  isModifierRequired,
  isModifierMultiSelect,
  getMaxOptionsToSelect,
  hasSubOptions,
  getSubOptions,
  createWholeOption,
  getSelectedSubOption
} from '../../utils/modifierUtils';
import { formatPrice, parsePrice } from '../../utils/priceUtils';
import { SubOptionSelector } from './SubOptionSelector';

interface ModifierOption {
  id: string | number;
  name: string;
  price: number | string;
}

interface Modifier {
  id?: string | number;
  name: string;
  options: ModifierOption[];
  min_quantity?: number;
  max_quantity?: number;
  max_options_to_select?: string | number;
}

interface ModifierGroupProps {
  modifier: Modifier;
  isExpanded: boolean;
  onToggle: () => void;
  onOptionSelect: (option: ModifierOption) => void;
  isOptionSelected: (optionName: string) => boolean;
  validationError?: string;
  onSubOptionSelect?: (modifierName: string, optionName: string, subSelection: string, subPrice: string) => void;
  selectedModifiers?: any[];
}

export const ModifierGroup: React.FC<ModifierGroupProps> = ({
  modifier,
  isExpanded,
  onToggle,
  onOptionSelect,
  isOptionSelected,
  validationError,
  onSubOptionSelect,
  selectedModifiers = [],
}) => {
  const contentId = useId();

  const required = isModifierRequired(modifier);
  const multiSelect = isModifierMultiSelect(modifier);

  // Calculate max options allowed using utility
  const maxNum = getMaxOptionsToSelect(modifier.max_options_to_select);

  // Count currently selected options
  const selectedCount = modifier.options.filter(opt => isOptionSelected(opt.name)).length;

  return (
    <section
      aria-labelledby={`${contentId}-title`}
      className="mb-4 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden"
    >
      {/* Toggle Button */}
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={onToggle}
        className={clsx(
          'w-full flex items-center justify-between text-left',
          'px-3 sm:px-4 py-2.5 sm:py-3',
          'hover:bg-gray-100 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500'
        )}
      >
        <h4
          id={`${contentId}-title`}
          className="font-medium text-gray-800 flex items-center gap-2 flex-wrap text-sm sm:text-base"
        >
          {modifier.name}
          {required ? (
            <span
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ backgroundColor: '#FB2B37', color: 'white' }}
            >
              Required
            </span>
          ) : (
            <span className="text-xs text-gray-500">(Optional)</span>
          )}
          <span className="text-xs text-gray-500">
            • Select up to {!multiSelect ? 1 : maxNum !== Infinity ? maxNum : modifier.options.length}
          </span>
        </h4>

        <MdExpandMore
          aria-hidden
          className={clsx(
            'h-5 w-5 text-gray-500 flex-shrink-0',
            'transition-transform duration-300 ease-in-out',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Animated Content */}
      <div
        id={contentId}
        role="region"
        className={clsx(
          'px-3 sm:px-4',
          'transition-[max-height,opacity,padding] duration-300 ease-in-out',
          isExpanded
            ? 'max-h-[800px] py-3 sm:py-4 opacity-100'
            : 'max-h-0 py-0 opacity-0 overflow-hidden'
        )}
      >
        <div className="space-y-2">
          {modifier.options.map((option: ModifierOption) => {
            // Check if this option should be disabled (max reached for unselected options)
            const isMaxReached = multiSelect && selectedCount >= maxNum && !isOptionSelected(option.name);
            
            // Safe sub-option detection
            const hasSubOpts = hasSubOptions(option);
            const isOptionCurrentlySelected = isOptionSelected(option.name);
            
            if (hasSubOpts && isOptionCurrentlySelected && onSubOptionSelect) {
              // ENHANCED: Integrated container for options with sub-options
              const availableSubOptions = getSubOptions(option);
              const wholeOption = createWholeOption(option);
              
              if (availableSubOptions.length > 0 && wholeOption) {
                return (
                  <div 
                    key={option.id ?? option.name}
                    className={clsx(
                      'w-full rounded-lg border text-left transition-all',
                      isOptionCurrentlySelected
                        ? 'border-2 bg-red-50'
                        : isMaxReached
                          ? 'bg-gray-100 border-gray-200 opacity-60'
                          : 'bg-white border-gray-200'
                    )}
                    style={{
                      borderColor: isOptionCurrentlySelected ? '#FB2B37' : undefined,
                    }}
                  >
                    {/* Main option header */}
                    <button
                      onClick={() => onOptionSelect(option)}
                      disabled={isMaxReached}
                      className="w-full p-3 min-h-[48px] flex items-center justify-between"
                      aria-pressed={isOptionCurrentlySelected}
                      aria-disabled={isMaxReached}
                    >
                      <div className="flex items-center">
                        <div className="mr-3">
                          {multiSelect ? (
                            <div
                              className="w-5 h-5 border-2 rounded flex items-center justify-center transition-colors"
                              style={{
                                backgroundColor: isOptionCurrentlySelected
                                  ? '#FB2B37'
                                  : 'transparent',
                                borderColor: isOptionCurrentlySelected ? '#FB2B37' : '#9CA3AF',
                              }}
                            >
                              {isOptionCurrentlySelected && (
                                <svg
                                  className="w-3 h-3 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                          ) : (
                            <div
                              className="w-5 h-5 border-2 rounded-full flex items-center justify-center"
                              style={{
                                borderColor: isOptionCurrentlySelected ? '#FB2B37' : '#9CA3AF',
                              }}
                            >
                              {isOptionCurrentlySelected && (
                                <div
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{ backgroundColor: '#FB2B37' }}
                                />
                              )}
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-sm text-gray-900">{option.name}</span>
                      </div>
                      <span className="text-sm text-gray-600 font-medium">
                        +{(() => {
                          // SAFE: Show dynamic price based on sub-option selection
                          try {
                            const currentSubOption = getSelectedSubOption(modifier.name, option.name, selectedModifiers);
                            
                            if (currentSubOption && currentSubOption !== "Whole") {
                              const subOpts = getSubOptions(option);
                              const subOpt = subOpts.find(sub => sub && sub.name === currentSubOption);
                              if (subOpt && subOpt.price) {
                                return formatPrice(parsePrice(subOpt.price));
                              }
                            }
                            
                            return formatPrice(parsePrice(option.price));
                          } catch (error) {
                            return formatPrice(parsePrice(option.price));
                          }
                        })()}
                      </span>
                    </button>
                    
                    {/* Sub-options INSIDE container */}
                    <div className="px-3 pb-3">
                      <SubOptionSelector
                        mainOption={option}
                        availableSubOptions={availableSubOptions}
                        selectedOption={getSelectedSubOption(modifier.name, option.name, selectedModifiers) || "Whole"}
                        modifierName={modifier.name}
                        optionName={option.name}
                        onOptionSelect={(selection, price) => {
                          try {
                            onSubOptionSelect(modifier.name, option.name, selection, price);
                          } catch (error) {
                            console.warn('Error handling sub-option selection:', error);
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              }
            }
            
            // NORMAL: Options without sub-options (unchanged behavior)
            return (
              <button
                key={option.id ?? option.name}
                onClick={() => onOptionSelect(option)}
                disabled={isMaxReached}
                className={clsx(
                  'w-full p-3 min-h-[48px] rounded-lg border text-left transition-all',
                  'flex items-center justify-between',
                  isOptionCurrentlySelected
                    ? 'border-2 bg-red-50'
                    : isMaxReached
                      ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed'
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                )}
                style={{
                  borderColor: isOptionCurrentlySelected ? '#FB2B37' : undefined,
                }}
                aria-pressed={isOptionCurrentlySelected}
                aria-disabled={isMaxReached}
              >
                <div className="flex items-center">
                  <div className="mr-3">
                    {multiSelect ? (
                      <div
                        className="w-5 h-5 border-2 rounded flex items-center justify-center transition-colors"
                        style={{
                          backgroundColor: isOptionCurrentlySelected
                            ? '#FB2B37'
                            : 'transparent',
                          borderColor: isOptionCurrentlySelected ? '#FB2B37' : '#9CA3AF',
                        }}
                      >
                        {isOptionCurrentlySelected && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    ) : (
                      <div
                        className="w-5 h-5 border-2 rounded-full flex items-center justify-center"
                        style={{
                          borderColor: isOptionCurrentlySelected ? '#FB2B37' : '#9CA3AF',
                        }}
                      >
                        {isOptionCurrentlySelected && (
                          <div
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: '#FB2B37' }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <span className="font-medium text-sm text-gray-900">{option.name}</span>
                </div>
                <span className="text-sm text-gray-600 font-medium">
                  +{formatPrice(parsePrice(option.price))}
                </span>
              </button>
            );
          })}
        </div>

        {validationError && (
          <p className="text-red-500 text-xs mt-2 font-medium" role="alert">
            {validationError}
          </p>
        )}
      </div>
    </section>
  );
};

ModifierGroup.displayName = 'ModifierGroup';

export default ModifierGroup;
